import type React from "react";
import {memo, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {ScrollView} from "react-native";

import {useView} from "../../hooks/useView";
import {useDisclose} from "../../react";
import {AddIcon} from "../../react/components/Icons/AddIcon";
import {UserContext} from "../../react/contexts/UserContext";
import {Modal} from "../Modal";
import SearchInput from "../SearchInput";
import {Button, ButtonIcon, ButtonText} from "../ui/button";
import {Center} from "../ui/center";
import type {IMenuProps} from "../ui/menu";
import {Menu, MenuItem} from "../ui/menu";
import {Text} from "../ui/text";
import {VStack} from "../ui/vstack";

import TagOptionItem from "./TagOptionItem";
import type {SelectTagEvent, TagOption} from "./types";
import {updateOptions} from "./util";

type Selection = "all" | Set<string | number>;

export type Props = {
    event: SelectTagEvent;
    onSelectedOption: (option: TagOption) => void;
};

const SelectMessageTags: React.FC<Props> = ({event, onSelectedOption}) => {
    const {tags} = useContext(UserContext);
    const [options, setOptions] = useState<TagOption[]>();
    const [searchText, setSearchText] = useState<string>("");
    const {isOpen, onClose, onOpen} = useDisclose();
    const view = useView();

    const optionsFiltered = useMemo(
        () =>
            options?.filter(
                option =>
                    option.label.toLowerCase().includes(searchText?.toLocaleLowerCase() || "") ||
                    option.value.toLowerCase().includes(searchText?.toLocaleLowerCase() || "")
            ),
        [options, searchText]
    );

    const triggerListingDropdown: IMenuProps["trigger"] = useCallback(triggerProps => {
        return (
            <Button {...triggerProps} no110MinWidth size="sm" testID="btn-add-tag" variant="ghost">
                <ButtonIcon as={AddIcon} />
                <ButtonText>Add Tag</ButtonText>
            </Button>
        );
    }, []);

    const handleSelectionChange = useCallback(
        (keys: Selection) => {
            if (keys !== "all") {
                const value = keys.values().toArray()[0];
                const option = options?.find(option => option.value === value);
                if (option) {
                    onSelectedOption(option);
                    setSearchText("");
                    onClose();
                }
            }
        },
        [onClose, onSelectedOption, options]
    );

    const handleSelectItem = useCallback(
        (item: TagOption) => {
            onSelectedOption(item);
            setSearchText("");
            onClose();
        },
        [onClose, onSelectedOption]
    );

    const handleClearSearch = useCallback(() => setSearchText(""), []);

    useEffect(() => {
        if (tags) {
            setOptions(updateOptions(event, tags));
        }
    }, [event, tags]);

    if (process.env.IS_WEB && view === "webDesktop") {
        return (
            <Menu
                className="pb-3"
                closeOnSelect={false}
                isOpen={isOpen}
                selectionMode="single"
                testID="select-tags"
                trigger={triggerListingDropdown}
                onClose={onClose}
                onOpen={onOpen}
                onSelectionChange={handleSelectionChange}
            >
                <MenuItem disabled className="border-b-0 mx-1 py-2 px-2">
                    <SearchInput
                        autofocus
                        query={searchText}
                        testID="input-search-tag"
                        onChangeText={setSearchText}
                        onClear={handleClearSearch}
                    />
                </MenuItem>
                {optionsFiltered?.map(option => (
                    <MenuItem
                        key={option.value}
                        className="mx-1 p-0 border-b-0"
                        textValue={option.value}
                    >
                        <TagOptionItem option={option} />
                    </MenuItem>
                ))}
                {optionsFiltered?.length === 0 && (
                    <Center className="p-2">
                        <Text variant="sm">No tags found</Text>
                    </Center>
                )}
            </Menu>
        );
    }

    return (
        <>
            <Button no110MinWidth size="sm" testID="btn-add-tag" variant="ghost" onPress={onOpen}>
                <ButtonIcon as={AddIcon} />
                <ButtonText>Add Tag</ButtonText>
            </Button>
            <Modal isOpen={isOpen} responsive={false} size="xl" title="Add tag" onClose={onClose}>
                <ScrollView style={{maxHeight: 500}}>
                    <VStack space="md">
                        <SearchInput
                            autofocus
                            className="w-full"
                            query={searchText}
                            testID="input-search-tag"
                            onChangeText={setSearchText}
                            onClear={handleClearSearch}
                        />
                        <VStack className="w-full">
                            {optionsFiltered?.map(option => (
                                <TagOptionItem
                                    key={option.value}
                                    option={option}
                                    onSelect={handleSelectItem}
                                />
                            ))}
                            {optionsFiltered?.length === 0 && (
                                <Center className="p-2">
                                    <Text variant="sm">No tags found</Text>
                                </Center>
                            )}
                        </VStack>
                    </VStack>
                </ScrollView>
            </Modal>
        </>
    );
};

export default memo(SelectMessageTags);
